import G6 from '@antv/g6';
import omit from 'lodash/omit';
import React from 'react';

import { hexToRgba } from 'utils/convert';
import { Graph, Item, Shape, ShapeElement } from '../../types';

interface InlineEditStyles {
  wrapper: React.CSSProperties;
  input: React.CSSProperties;
  placeholder: React.CSSProperties;
}

const parseShapeStyle = (style: Record<string, any>) => {
  if (!style) return {};

  const { fill, fillOpacity, lineHeight, ...rest } = style;
  const additionalStyles: Record<string, any> = {};

  if (fill) {
    additionalStyles.color = fillOpacity ? hexToRgba(fill, fillOpacity) : fill;
  }
  // For canvas, line height of 16 means 16px
  // In html, line height of 16 means 16 times the font size
  // This makes sure that it is appropriately converted
  if (typeof lineHeight === 'number') {
    additionalStyles.lineHeight = `${lineHeight}px`;
  }

  return {
    ...omit(rest, [
      'baseStyle',
      'emptyStyle',
      'inlineEdit',
      'lineAppendWidth',
      'margin',
      'matrix',
      'placeholder',
      'stroke',
      'strokeOpacity',
      'text',
    ]),
    ...additionalStyles,
  };
};

export const getStyles = ({
  graph,
  shape,
  item,
}: {
  graph: Graph;
  shape: Shape;
  item: Item;
}): InlineEditStyles => {
  // Get the shape offset
  const { x: relativeX, y: relativeY, width: minWidth, height: minHeight } = shape.getBBox();
  const { x: absoluteX, y: absoluteY } = G6.Util.applyMatrix(
    { x: relativeX, y: relativeY },
    item.getContainer().getMatrix(),
  );
  const { x: left, y: top } = graph.getCanvasByPoint(absoluteX, absoluteY);
  const { attrs = {} } = shape as ShapeElement;
  const styleAttrs = parseShapeStyle({ ...attrs, ...(attrs.inlineEdit || {}) });

  return {
    wrapper: {
      top,
      left,
      minWidth,
      minHeight,
      transform: `scale(${graph.getZoom()})`,
      position: 'absolute',
      transformOrigin: 'left top',
      ...styleAttrs,
    },
    input: {
      ...styleAttrs,
      minWidth,
      minHeight,
    },
    placeholder: parseShapeStyle(attrs.placeholderStyle),
  };
};

export const updateShapeVisibility = (shape: Shape | null, isVisible: boolean) => {
  if (shape) {
    shape.set('visible', isVisible);
  }
};
